import { INSERT_CHECK_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { HeadingTagType } from '@lexical/rich-text';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import type { TextFormatType } from 'lexical';
import { $createParagraphNode, $getSelection, $isRangeSelection, $isTextNode, FORMAT_TEXT_COMMAND } from 'lexical';
import { useCallback } from 'react';

import { $setListNumbering } from '../list-numbering/listNumbering';
import type { BlockType } from './useToolbarState';

const LIST_COMMANDS = {
  bullet: INSERT_UNORDERED_LIST_COMMAND,
  number: INSERT_ORDERED_LIST_COMMAND,
  check: INSERT_CHECK_LIST_COMMAND,
} as const;

/**
 * The toolbar's formatting actions, given the caret's current block type.
 * - `setBlock` toggles: applying the block type that's already set returns to a paragraph (or removes the list).
 * - Switching between plain and hierarchical numbering restyles the whole list instead.
 * - `clearFormatting` strips inline formatting only, like Froala: a heading stays a heading.
 */
export const useBlockActions = (currentBlock: BlockType) => {
  const [editor] = useLexicalComposerContext();

  const format = useCallback((type: TextFormatType) => editor.dispatchCommand(FORMAT_TEXT_COMMAND, type), [editor]);

  const setBlock = useCallback(
    (target: BlockType) => {
      if (target === 'hierarchical') {
        if (currentBlock === target) editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        else editor.update(() => $setListNumbering('hierarchical'));
        return;
      }
      if (target === 'number' && currentBlock === 'hierarchical') {
        editor.update(() => $setListNumbering('flat'));
        return;
      }

      if (target === 'bullet' || target === 'number' || target === 'check') {
        editor.dispatchCommand(currentBlock === target ? REMOVE_LIST_COMMAND : LIST_COMMANDS[target], undefined);
        return;
      }

      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        if (currentBlock === target || target === 'paragraph') $setBlocksType(selection, () => $createParagraphNode());
        else if (target === 'quote') $setBlocksType(selection, () => $createQuoteNode());
        else $setBlocksType(selection, () => $createHeadingNode(target as HeadingTagType));
      });
    },
    [currentBlock, editor],
  );

  const clearFormatting = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      for (const node of selection.getNodes()) {
        if (!$isTextNode(node)) continue;
        node.setFormat(0);
        node.setStyle('');
      }
    });
  }, [editor]);

  return { format, setBlock, clearFormatting };
};
